/**
 * Pure geometry functions for building GeoJSON from drawing points.
 * Extracted from LinePolygonDrawingManager for testability.
 */

export interface LngLatLike {
  lng: number
  lat: number
}

/**
 * Build a GeoJSON LineString Feature from an array of points.
 * Returns undefined if fewer than 2 points are provided.
 */
export function buildLineStringFeature(
  points: LngLatLike[]
): GeoJSON.Feature<GeoJSON.LineString> | undefined {
  if (points.length < 2) {
    return undefined
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: points.map(p => [p.lng, p.lat]),
    },
    properties: {},
  }
}

/**
 * Build a GeoJSON Polygon Feature from an array of points.
 * Automatically closes the ring (first == last coordinate).
 * Returns undefined if fewer than 3 points are provided.
 */
export function buildPolygonFeature(
  points: LngLatLike[]
): GeoJSON.Feature<GeoJSON.Polygon> | undefined {
  if (points.length < 3) {
    return undefined
  }

  const ring = closeRing(points.map(p => [p.lng, p.lat]))
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [ring],
    },
    properties: {},
  }
}

/**
 * Close a coordinate ring by appending the first coordinate if needed.
 * A valid GeoJSON polygon ring must have first == last coordinate.
 */
export function closeRing(coords: number[][]): number[][] {
  if (coords.length === 0) {
    return []
  }

  const first = coords[0]
  const last = coords[coords.length - 1]

  if (first[0] === last[0] && first[1] === last[1]) {
    return coords
  }

  return [...coords, [...first]]
}

/**
 * Build a GeoJSON LineString Feature representing a preview line
 * from the last drawing point to the current cursor position.
 * Returns undefined if no points are provided.
 */
export function buildPreviewLineFeature(
  lastPoint: LngLatLike,
  cursorPos: LngLatLike
): GeoJSON.Feature<GeoJSON.LineString> {
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [
        [lastPoint.lng, lastPoint.lat],
        [cursorPos.lng, cursorPos.lat],
      ],
    },
    properties: {},
  }
}

/**
 * Build a GeoJSON Polygon Feature for polygon preview that includes
 * the cursor position and closes back to the first point.
 * Returns undefined if fewer than 2 existing points.
 */
export function buildPolygonPreviewFeature(
  points: LngLatLike[],
  cursorPos: LngLatLike
): GeoJSON.Feature<GeoJSON.Polygon> | undefined {
  if (points.length < 2) {
    return undefined
  }

  const allCoords = points.map(p => [p.lng, p.lat])
  allCoords.push([cursorPos.lng, cursorPos.lat])
  allCoords.push(allCoords[0])

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [allCoords],
    },
    properties: {},
  }
}

/**
 * Convert an array of LngLatLike points to coordinate arrays [lng, lat].
 */
export function pointsToCoordinates(points: LngLatLike[]): number[][] {
  return points.map(p => [p.lng, p.lat])
}
